import React, { useState, useEffect } from "react";
import { ReactFlow, ReactFlowProvider, addEdge, Controls, Node, Edge, MiniMap, Panel } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ConnectedEdge } from "../hooks/ConnectedEdge";
import ResizableNodeSelected from "../hooks/ResizebleNodeSelected";
import CustomNode from "../hooks/CustomNode";
import ButtonEdge from "../hooks/ButtonEdge";
import TPNode from "../hooks/TPNode";
import TCNode from "../hooks/TCNode";
import ChaSec from "../hooks/ChaSecNode";
import GroupNode from "../hooks/GroupNode";
import RedeEquatorial from "../hooks/RedeEquatorial";
import RulerNode from "../hooks/RulerNode";
import { DisconnectedEdge } from "../hooks/DisconnectedEdge";
import { OfflineEdge } from "../hooks/OfflineEdge";
import MotorizadoNode from "../hooks/MotorizadoNode";
import axios from "axios";
import LegendNode from "../hooks/LegendNode";
import { CustomEdge } from "../hooks/CustomEdge";
import DjNode from "../hooks/DjNode";
import config from "../config";

type WorkspaceProps = {
  nodes: Node[];
  handleNodeClick: (_: any, node: any) => void;
  edges: Edge[];
  setNodes: any;
  setEdges: any;
  onNodesChange: any;
  onEdgesChange: any;
  images: { [key: string]: string };
  setTrackersData: any;
  tempoColeta: any;
  usina: string;
  nomeUsina: string;
};

const Workspace: React.FC<WorkspaceProps> = ({ nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange, images, handleNodeClick, setTrackersData, tempoColeta, usina, nomeUsina }) => {
  const edgeTypes = {
    ConnectedEdge: ConnectedEdge,
    DisconnectedEdge: DisconnectedEdge,
    buttonEdge: ButtonEdge,
    OfflineEdge: OfflineEdge,
    customEdge: CustomEdge,
  };
  const nodeTypes = {
    ResizableNodeSelected,
    CustomNode,
    TPNode,
    TCNode,
    ChaSec,
    GroupNode,
    RedeEquatorial,
    RulerNode,
    MotorizadoNode,
    LegendNode,
    DjNode,
  };
  let disconnectedItems = new Set<string>();
  let offlineItems = new Set<string>();

  const findDescendants = (parentId: string): string[] => {
    const descendants: Set<string> = new Set();
    const stack: string[] = [parentId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId) continue;

      // Encontre todas as edges que saem do currentId
      const children = edges.filter((edge) => edge.source === currentId).map((edge) => edge.target);

      for (const child of children) {
        if (!descendants.has(child)) {
          descendants.add(child);
          stack.push(child); // Continue explorando os descendentes do filho
        }
      }
    }

    return Array.from(descendants);
  };

  const handleNodeStatusChange = (nodeId: string, edgeType: string) => {
    const descendantIds = findDescendants(nodeId);
    if (edgeType === "OfflineEdge") {
      offlineItems = new Set([...Array.from(offlineItems), ...descendantIds]);
    } else if (edgeType === "DisconnectedEdge") {
      disconnectedItems = new Set([...Array.from(disconnectedItems), ...descendantIds]);
      if (offlineItems.has(nodeId)) {
        return;
      }
    } else if (edgeType === "ConnectedEdge") {
      if (disconnectedItems.has(nodeId) || offlineItems.has(nodeId)) {
        return;
      }
    }

    setEdges((prevEdges: any) =>
      prevEdges.map((edge: any) => {
        if (descendantIds.includes(edge.target)) {
          return { ...edge, type: edgeType };
        }
        return edge;
      })
    );
  };
  const fitViewOptions = { padding: 4 };

  useEffect(() => {
    const buscarDadosScada = () => {
      axios
        .get("https://apisupervisorio.pvoperation.com/dados")
        .then((response) => {
          disconnectedItems = new Set<string>();
          offlineItems = new Set<string>();
          let trackersData = response.data.filter((device: any) => device.sn.includes("TRK"));
          setTrackersData(trackersData);
          DataValidationScada(response);
        })
        .catch((error) => console.error("Erro ao buscar dados:", error));
    };

    const buscarDadosPlataforma = () => {
      axios
        .get(`https://apiplataforma.pvoperation.com/v2/scada?usina=${usina}`)
        .then((response) => {
          disconnectedItems = new Set<string>();
          offlineItems = new Set<string>();
          // let trackersData = response.data.filter((device: any) =>
          //   device.sn.includes("TRK")
          // );
          // setTrackersData(trackersData);
          DataValidationPlatform(response);
        })
        .catch((error) => console.error("Erro ao buscar dados:", error));
    };

    const func = config.plataforma ? buscarDadosPlataforma : buscarDadosScada;
    const intervalMiliSeconds = config.plataforma ? 30000 : 5000;
    setTimeout(() => func(), config.plataforma ? 1000 : intervalMiliSeconds);
    const interval = setInterval(func, intervalMiliSeconds);

    return () => clearInterval(interval);
  }, []);

  const DataValidationPlatform = (response: any) => {
    setNodes((nds: Node[]) =>
      nds.map((node) => {
        const array = node.data.type === "inversor"
          ? response.data.inversores : node.data.type !== "combiner"
          ? response.data.dispositivos : response.data.combiners;
        const device = array.find((d: any) => d.esn === node.data.esn);

        if (node.type === "RulerNode") {
          const deviceRuler = response.data.dispositivos.find((d: any) => d.esn === node.data.esn);
          const invRuler = response.data.inversores.find((d: any) => d.esn === node.data.esn);
          const combinerRuler = response.data.combiners ? response.data.combiners.find((d: any) => d.esn === node.data.esn) : {};
          node.data.leitura = deviceRuler
            ? deviceRuler?.leitura : invRuler
            ? invRuler?.leitura : combinerRuler?.leitura;
        }
        if (device) {
          let updatedNode = {
            ...node,
            data: {
              ...node.data,
              ...device,
            },
          };
          const ultimaLeitura = device.leitura || {};
          updatedNode.data.leitura = ultimaLeitura;
          updatedNode.data.last_state = ultimaLeitura;

          const dateLastSent = device?.tsleitura ? new Date(device.tsleitura.split("GMT")[0]) : null;
          const actualDate = new Date();

          if (dateLastSent) {
            const timeDifferenceInMillis = actualDate.getTime() - dateLastSent.getTime();
            const timeDifferenceInSeconds = timeDifferenceInMillis / 1000;
            let valorFalha = 720;
            if (node.data.type === "inversor") {
              valorFalha = tempoColeta.inversor;
            } else if (node.data.type === "estacao") {
              valorFalha = tempoColeta.meteo;
            } else if (node.data.type === "medidorFronteira") {
              valorFalha = tempoColeta.meter;
            } else if (node.data.type === "relesProtecao") {
              valorFalha = tempoColeta.rele;
            } else if (node.data.type === "relesTemperatura") {
              valorFalha = tempoColeta.reletemp;
            } else if (node.data.type === "combiner") {
              valorFalha = tempoColeta.combiner;
            }
            valorFalha *= 60;
            if (device.status === 2) {
              updatedNode.data.falha = true;
            }
            if (timeDifferenceInSeconds > valorFalha) {
              handleNodeStatusChange(node.id, "OfflineEdge");
              updatedNode.data.falhaAtraso = true;
              updatedNode.data.atraso = timeDifferenceInSeconds / 60;
            } else {
              updatedNode.data.falhaAtraso = false;
              updatedNode.data.atraso = 0;
            }
            if (ultimaLeitura?.rVfaseA === 0 && updatedNode.data.type === "relesProtecao") {
              handleNodeStatusChange(node.id, "DisconnectedEdge");
            } else {
              handleNodeStatusChange(node.id, "ConnectedEdge");
            }
          }
          return updatedNode;
        }

        return node;
      })
    );
  };

  const DataValidationScada = (response: any) => {
    setNodes((nds: Node[]) =>
      nds.map((node) => {
        const device = response.data.find((d: any) => d.sn === node.data.esn);
        if (device) {
          // Atualize o node com os dados do dispositivo
          let updatedNode = {
            ...node,
            data: {
              ...node.data,
              ...device,
            },
          };
          const ultimaLeitura = device.last_state || {};
          updatedNode.data.leitura = ultimaLeitura;

          // Verifique o status da última leitura
          const dateLastSent = device?.last_sent ? new Date(device.last_sent) : null;
          const actualDate = new Date();

          if (dateLastSent) {
            const timeDifferenceInMillis = actualDate.getTime() - dateLastSent.getTime();
            const timeDifferenceInSeconds = timeDifferenceInMillis / 1000;
            if (timeDifferenceInSeconds > 30) {
              handleNodeStatusChange(node.id, "OfflineEdge");
            } else if (ultimaLeitura?.rVfaseA === 0 || ultimaLeitura?.Uac === 0) {
              handleNodeStatusChange(node.id, "DisconnectedEdge");
            } else {
              handleNodeStatusChange(node.id, "ConnectedEdge");
            }
          }

          // Atualize o ícone da chave seccionadora
          if (node.type === "ChaSec") {
            // if (!ultimaLeitura?.state) {
            //   handleNodeStatusChange(node.id, "DisconnectedEdge");
            // }
            updatedNode = {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                icone: ultimaLeitura?.state ? "sec-fechado.png" : "sec-aberto.png",
              },
            };
          }
          if (node.type == "DjNode") {
            // if (!ultimaLeitura?.state) {
            //   handleNodeStatusChange(node.id, "DisconnectedEdge");
            // }
            updatedNode = {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                icone: ultimaLeitura?.state ? "fechado.png" : "aberto.png",
              },
            };
          }
          if (device?.status) {
            updatedNode = {
              ...updatedNode,
              data: {
                ...updatedNode.data,
                falha: true,
              },
            };
          }
          return updatedNode;
        }

        return node; // Retorne o node sem alterações se não houver correspondência
      })
    );
  };

  return (
    <div className="h-full w-full">
      <ReactFlowProvider>
        <div className="relative flex-1 bg-gray-100 h-full">
          <ReactFlow
            nodes={nodes}
            nodeTypes={nodeTypes}
            edges={edges}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={fitViewOptions}
            contentEditable={false}
            nodesDraggable={false}
            elementsSelectable={false}
            draggable={false}
            minZoom={0.2}
          >
            <Controls showInteractive={false} />
            {config.plataforma && nomeUsina != "" ? (
              <Panel position="top-right">
                <div className="p-2 bg-white rounded shadow-md">
                  <h2 className="text-lg font-semibold text-gray-800">{nomeUsina}</h2>
                </div>
              </Panel>
            ) : null}
          </ReactFlow>
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default Workspace;
